import { useMemo, useState } from "react";

import {
  FieldComponentOverrideProps,
  FieldProps,
  FieldGroup,
  Label,
  FieldWrapper,
  fieldName,
} from "@smart/bridge-intake-components-dom";
import { useDebouncedValue } from "@smart/itops-hooks-dom";
import { SearchFieldInput } from "@smart/itops-sb-design-system-dom";

import {
  findMatchingContacts,
  findRelatedContactFields,
  getContactFieldKey,
  formatContactDisplayName,
  ContactData,
} from "./utils";

type ContactSearchFieldProps = FieldProps<HTMLInputElement> &
  FieldComponentOverrideProps & {
    contactType: "person" | "company";
  };

export const ContactSearchField = ({
  contactType,
  field,
  index,
  innerRef,
  value,
  error,
  loading,
  disabled,
  onChange,
  onBlur,
  fields,
  submitResponses,
  responses,
  setResponses,
  setFormValue,
}: ContactSearchFieldProps) => {
  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  const [lastAction, setLastAction] = useState<
    "initial" | "select" | "type" | "blur"
  >("initial");

  const searchValue = useDebouncedValue(
    lastAction !== "initial" ? (value ?? "") : "",
    300,
  );

  const matches = useMemo(() => {
    if (lastAction !== "type" || !searchValue) {
      return [];
    }
    return findMatchingContacts({
      searchValue,
      fields,
      responses,
      currentField: field,
      contactType,
    });
  }, [searchValue, fields, responses, field, index, lastAction, contactType]);

  const getItems = () => {
    if (lastAction !== "type" || matches.length === 0) {
      return [];
    }

    return matches.map((contact: ContactData) => {
      const label = formatContactDisplayName(contact, contactType);

      return {
        label,
        onClick: () => {
          const relatedFields = findRelatedContactFields(
            field,
            fields,
            contactType,
          );
          const responsesToUpdate = relatedFields.reduce(
            (acc, relatedField) => {
              const fieldKey = getContactFieldKey(
                relatedField.field?.name || "",
                contactType,
              );
              if (!fieldKey || !contact[fieldKey]) return acc;

              const contactValue = contact[fieldKey];

              if (relatedField.groupUri && index !== undefined) {
                const existingResponse = responses[relatedField.uri];
                const updated = Array.isArray(existingResponse)
                  ? [...existingResponse]
                  : [];
                while (updated.length <= index) {
                  updated.push(null);
                }
                updated[index] = contactValue;
                acc[relatedField.uri] = updated;
              } else {
                acc[relatedField.uri] = contactValue;
              }

              return acc;
            },
            {} as Record<string, any>,
          );

          setLastAction("select");

          if (setFormValue) {
            Object.entries(responsesToUpdate).forEach(
              ([fieldUri, fieldValue]) => {
                const relatedField = fields.find((f) => f.uri === fieldUri);
                if (!relatedField) return;

                const isInGroup = relatedField.groupUri && index !== undefined;
                const valueToUpdate =
                  isInGroup && Array.isArray(fieldValue)
                    ? fieldValue[index]
                    : fieldValue;

                const fieldPath = fieldName({
                  field: relatedField,
                  index: isInGroup ? index : undefined,
                });

                setFormValue(fieldPath, valueToUpdate, {
                  shouldValidate: false,
                  shouldDirty: false,
                });
              },
            );
          }

          submitResponses({
            responses: responsesToUpdate,
          })
            .then((result) => {
              if (result?.operationIds && setResponses) {
                setResponses((current) => ({
                  ...current,
                  ...responsesToUpdate,
                }));
              }
            })
            .catch(console.error);
        },
      };
    });
  };

  return (
    <FieldWrapper disabled={disabled} isLoading={loading}>
      <FieldGroup
        id={id}
        hint={field.hint ?? undefined}
        label={<Label {...field} />}
      >
        <SearchFieldInput
          ref={innerRef}
          id={id}
          name={id}
          data-testid={id}
          error={!!error}
          message={error ?? undefined}
          aria-invalid={!!error}
          aria-errormessage={errorId ?? undefined}
          aria-autocomplete="list"
          value={value ?? ""}
          onChange={(e) => {
            if (lastAction !== "type") {
              setLastAction("type");
            }
            onChange(e.target.value);
          }}
          onBlur={() => {
            setLastAction("blur");
            onBlur();
          }}
          autoComplete="off"
          fetching={lastAction === "type" && matches.length > 0}
          onClear={() => {
            onChange("");
            onBlur();
          }}
          options={getItems()}
          leftIcon={null}
        />
      </FieldGroup>
    </FieldWrapper>
  );
};

export const getContactSearchFieldOverride =
  () => (fieldProps: FieldProps & FieldComponentOverrideProps) => {
    const { field } = fieldProps;

    const getContactType = (fieldNameToCheck: string | undefined) => {
      if (!fieldNameToCheck) return null;
      if (fieldNameToCheck === "person/firstName") return "person";
      if (fieldNameToCheck === "company/name") return "company";
      return null;
    };
    const contactType = getContactType(field.field?.name);

    if (field.type === "text" && !!contactType) {
      return <ContactSearchField {...fieldProps} contactType={contactType} />;
    }

    return undefined;
  };
